import React, { useEffect, useState } from "react";
import { ButtonSave } from "components/buttons";
import { UserAccess } from "components/user-access";
import { ListingTable } from "components/listing";
import {
    AlertUnsavedData,
    NotificationToaster,
} from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { FormSelect, FormSwitch } from "components/form-fields";
import { LicenceCheckingService } from "services";
import axios from "axios";
import { useDirtyData } from "hooks/useDirtyData";

export function LicenceCheckingAlerts() {
    const headers = ["alert", "description", "severity", "enabled"];
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [licenceCheckingOptions, setLicenceCheckingOptions] = useState([]);
    const [originalLicenceCheckingAlertsSettings, setOriginalLicenceCheckingAlertsSettings] = useState([]);
    const [licenceCheckingAlertsSettings, setLicenceCheckingAlertsSettings] = useState([]);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const isDataDirty = useDirtyData(
        originalLicenceCheckingAlertsSettings,
        licenceCheckingAlertsSettings
    );

    useEffect(initialLoad, []);

    useEffect(() => {
        if (
            !licenceCheckingAlertsSettings ||
            licenceCheckingAlertsSettings.length === 0 ||
            !licenceCheckingOptions ||
            licenceCheckingOptions.length === 0
        ) {
            return;
        }

        setTableRows(
            licenceCheckingOptions.alertTypes.map((alert) => {
                let alertSettings = licenceCheckingAlertsSettings.find(
                    (s) => s.alertTypeId === alert.id
                );
                let row = [
                    <p key={`${alert.key}-name`}>{alert.name}</p>,
                    <p key={`${alert.key}-description`}>{alert.description}</p>,
                ];

                if (alertSettings && alertSettings.configurable === false) {
                    row.push(
                        <p>
                            {
                                licenceCheckingOptions.alertSeverities.find(
                                    (s) =>
                                        s.id === alertSettings.alertSeverityId
                                ).name
                            }
                        </p>,
                        <p>On</p>
                    );
                } else {
                    row.push(
                        <FormSelect
                            id={`severity-${alert.id}`}
                            items={licenceCheckingOptions.alertSeverities}
                            selectedValue={
                                alertSettings?.alertSeverityId || null
                            }
                            disabled={!alertSettings}
                            onItemSelect={(item) =>
                                changeSeverity(alertSettings, item)
                            }
                        />,
                        <FormSwitch
                            id={`enabled-${alert.id}`}
                            checked={alertSettings}
                            onChange={(e) =>
                                enableOrDsiableAlert(
                                    alertSettings,
                                    alert,
                                    e.target.checked
                                )
                            }
                        />
                    );
                }

                return row;
            })
        );
        setTotalRecords(licenceCheckingAlertsSettings.length);
    }, [licenceCheckingOptions, licenceCheckingAlertsSettings]);

    function initialLoad() {
        axios
            .all([
                LicenceCheckingService.getLicenceCheckingOptions(),
                LicenceCheckingService.getLicenceCheckingAlertsSettings(),
            ])
            .then(
                axios.spread(function (options, settings) {
                    setLicenceCheckingOptions(options);
                    setOriginalLicenceCheckingAlertsSettings(settings);
                    setLicenceCheckingAlertsSettings(settings);
                }),
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not load licence checking alert settings. Please refresh the page."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not load licence checking alert settings. Please refresh the page."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function enableOrDsiableAlert(alertSettings, alert, enabled) {
        if (!enabled) {
            setLicenceCheckingAlertsSettings((prevValue) => {
                return prevValue.filter(
                    (s) => s.alertTypeId !== alertSettings.alertTypeId
                );
            });
        } else {
            setLicenceCheckingAlertsSettings((prevValue) => {
                return [
                    ...prevValue,
                    {
                        configurable: true,
                        alertTypeId: alert.id,
                        alertSeverityId: alert.severity,
                    },
                ];
            });
        }
    }

    function changeSeverity(alertSettings, item) {
        setLicenceCheckingAlertsSettings((prevValue) => {
            return prevValue.map((s) => {
                if (s.alertTypeId === alertSettings.alertTypeId) {
                    return {
                        ...s,
                        alertSeverityId: item.id,
                    };
                }
                return s;
            });
        });
    }

    function saveChanges() {
        setIsSaving(true);

        LicenceCheckingService.saveLicenceCheckingAlertsSettings(
            licenceCheckingAlertsSettings
        )
            .then(
                () => {
                    NotificationToaster.show(
                        Intent.SUCCESS,
                        "Licence checking alert settings saved successfully."
                    );
                    setOriginalLicenceCheckingAlertsSettings(licenceCheckingAlertsSettings);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not save licence checking alert settings. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not save licence checking alert settings. Please try again."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    return (
        <UserAccess
            perform={"LicenceChecking:Manage"}
            yes={() => (
                <div className="row">
                    <h2>Alerts</h2>

                    <AlertUnsavedData
                        isDirty={isDataDirty}
                        scrollToBottomOnCancel={true}
                    />

                    <ListingTable
                        id="licence-checking-alerts"
                        headers={headers}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={isLoading}
                        showPerPage={false}
                        pageable={false}
                    />

                    <div className="spacer-top">
                        <ButtonSave
                            onClick={saveChanges}
                            disabled={isSaving}
                            simpleDisabled={!isDataDirty}
                            loading={isLoading}
                        ></ButtonSave>
                    </div>
                </div>
            )}
        />
    );
}
